import { defineStore } from "pinia";
import type { HospitalStatus, HospitalStatuses } from "@/App/types/twiageTypes";
import PubnubServiceImpl from "@/App/services/PubnubService";
import { StatusesService } from "@/Pages/Statuses/statuses.service";
import { useCasesStore } from "@/Pages/Cases/store";
import { CaseMode } from "@/App/types/CaseMode";
import { StatusType } from "@/Pages/Statuses/StatusType";

export type StatusStore = {
  statuses: HospitalStatuses[];
  allHospitals: HospitalStatuses[]; // Store ALL hospitals, including those without active statuses
};

const HospitalStatusUpdatesType = "HOSPITAL_STATUS_UPDATED";

const activeStatusesNotEmpty = (hospitalStatuses: HospitalStatuses): boolean =>
  hospitalStatuses.activeStatuses?.length > 0;
const updateStatusesLocally = ({
  store,
  hospitalStatuses,
  clearBeforeAdd = false,
}: {
  store: StatusStore;
  hospitalStatuses: HospitalStatuses[];
  clearBeforeAdd?: boolean;
}): void => {
  if (clearBeforeAdd) {
    store.statuses = [];
  } else {
    const hospitalIds = hospitalStatuses.map((hospitalStatuses) => hospitalStatuses.id);
    store.statuses = store.statuses.filter((status) => !hospitalIds.includes(status.id));
  }
  store.statuses.push(...hospitalStatuses.filter(activeStatusesNotEmpty));
};

const getStatusesByType = (hospital: HospitalStatuses, caseMode: CaseMode) => {
  return hospital.activeStatuses.filter((status) => {
    switch (caseMode) {
      case CaseMode.ift:
        return status.type === StatusType.IFT;
      default:
        return status.type === undefined || status.type === StatusType.ED;
    }
  });
};

export const useStatusesStore = defineStore({
  id: "statuses",
  state: (): StatusStore => ({
    statuses: [],
    allHospitals: [],
  }),
  getters: {
    statusesForBar(state) {
      return state.statuses.flatMap((hospital) => {
        const statuses = getStatusesByType(hospital, useCasesStore().mode);
        if (statuses.length === 0) return [];
        return [{ ...hospital, statuses }];
      });
    },

    hospitalsForRegionalView(state) {
      return state.allHospitals.map((hospital) => {
        const statuses = getStatusesByType(hospital, useCasesStore().mode);
        return { ...hospital, statuses };
      });
    },
  },
  actions: {
    async fetchStatuses() {
      try {
        const hospitalsData = await StatusesService.getHospitalsStatuses();

        this.allHospitals = hospitalsData;

        updateStatusesLocally({
          store: this,
          hospitalStatuses: hospitalsData,
          clearBeforeAdd: true,
        });

        const channels = hospitalsData.map((status) => status.statusChannelId);
        PubnubServiceImpl.getInstance().subscribe<string>({
          channels,
          types: [HospitalStatusUpdatesType],
          callback: () => {
            this.updateStatuses();
          },
        });
      } catch (error) {
        console.log(error);
      }
    },

    async updateStatuses() {
      const hospitalData = await StatusesService.getHospitalsStatuses();

      this.allHospitals = hospitalData;

      updateStatusesLocally({ store: this, hospitalStatuses: hospitalData });
    },

    onSessionEnd() {
      this.statuses = [];
      this.allHospitals = [];
    },

    iftStatusesByHospitalId(hospitalId: string): HospitalStatus[] {
      return (
        this.statuses
          .find((hospital: HospitalStatuses) => hospital.id === hospitalId)
          ?.activeStatuses.filter((status) => status.type === StatusType.IFT) ?? []
      );
    },
  },
});
